import { lazy, Suspense } from "react"
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"

import { RouterContext } from "../main"
import { DialogQueueProvider } from "@src/context/DialogQueueProvider"
import { CookieConsent } from "@src/components/organisms/CookieConsent"
import { SkipLink } from "@src/components/atoms/SkipLink"
import { LanguageApplicationWrapper } from "@src/context/Languages"
import { getPublicTenantSettingsQuery } from "@src/api/tenantSettings"
import { useAppStore } from "@src/state"
import { useFocusPageOnNavigate } from "@src/hooks/useFocusPageOnNavigate"

// Only load Tanstack router devtools in development mode
const LazyTanStackRouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/router-devtools").then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    )

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData(
        getPublicTenantSettingsQuery(),
      )
      const extensions = (data.allowedFileExtensions ?? "")
        .split(",")
        .map((ext) => ext.trim().toLowerCase().replace(/^\./, ""))
        .filter(Boolean)
      useAppStore.getState().setAllowedFileExtensions(extensions)
    } catch {
      // If fetch fails, app continues with empty array (no validation blocks uploads)
    }
  },
  // Wrapping the root route in the dialog provider because in the main.tsx the dialog provider
  // would wrap the Router one and any router methods could not be used in dialogs
  component: RootComponent,
})

function RootComponent() {
  useFocusPageOnNavigate()

  return (
    <LanguageApplicationWrapper>
      <DialogQueueProvider>
        <SkipLink />
        <Outlet />
        <CookieConsent />
        <Suspense>
          <LazyTanStackRouterDevtools initialIsOpen={false} />
        </Suspense>
      </DialogQueueProvider>
    </LanguageApplicationWrapper>
  )
}
